import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import type { FAQPage, LocalBusiness, WithContext } from "schema-dts";
import type { FaqItem } from "../components/faqs/faqs.data";

import { seoConfig } from "./seo.config";

const HomePageSeo: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as string;

  const pageTitle = t("seo.title");
  const pageDescription = t("seo.description");
  const canonicalUrl = `${seoConfig.siteUrl}/${lang}/`;

  const localBusinessSchemas: Array<WithContext<LocalBusiness>> =
    seoConfig.business.branches.map((branch, index) => {
      const branchName =
        index === 0
          ? t("contact.sheungWan.name")
          : t("contact.causewayBay.name");

      return {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "@id": branch.id,
        name: `${seoConfig.business.name} - ${branchName}`,
        image: seoConfig.defaultImage,
        url: canonicalUrl,
        telephone: branch.phone,
        email: seoConfig.business.email,
        address: {
          "@type": "PostalAddress",
          streetAddress: branch.address,
          addressLocality: "Hong Kong",
          addressRegion: "HK",
          addressCountry: "HK",
        },
        areaServed: "Hong Kong",
        hasMap: branch.mapUrl,
        sameAs: seoConfig.business.sameAs,
        makesOffer: {
          "@type": "Offer",
          availability: "https://schema.org/InStock",
          url: branch.bookingUrl,
        },
      };
    });

  const faqs: FaqItem[] = t("faqs.items", { returnObjects: true }) as FaqItem[];

  const faqSchema: WithContext<FAQPage> = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(({ question, answer }) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: {
        "@type": "Answer",
        text: answer,
      },
    })),
  };

  const jsonLdScripts = [...localBusinessSchemas, faqSchema].map((schema) => ({
    type: "application/ld+json",
    innerHTML: JSON.stringify(schema),
  }));

  return (
    <Helmet script={jsonLdScripts}>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:type" content="website" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={seoConfig.defaultImage} />

      <meta name="twitter:card" content={seoConfig.twitterCardType} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={seoConfig.defaultImage} />
    </Helmet>
  );
};

export default HomePageSeo;
